import { _js } from '@ifixit/utils/localize';
import { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { color } from '@ifixit/primitives';
import { FaIcon } from '@ifixit/ui/components/icon';
import { resolveFaLegacyIcon } from 'Shared/faLegacyIcon';
import { relativeDate as getRelativeDate, MINUTE } from 'Shared/realtime_date';

const Icon = styled.span`
   width: 25px;
   margin-top: 3px;
   margin-bottom: auto;

   text-align: center;

   opacity: 0.3;
   transition:
      color 0.2s,
      opacity 0.2s;
`;

const NotificationInfo = styled.div`
   display: flex;
   flex-flow: column nowrap;

   margin-left: 10px;
   flex: 1;

   text-align: left;
   overflow: hidden;
   overflow-wrap: break-word;

   time {
      opacity: 0.4;
   }

   &:hover,
   &:active {
      outline: none;
   }
`;

const NotificationLink = styled.a`
   display: flex;
   flex-flow: row nowrap;

   padding: 10px;

   color: ${color.gray[800]};

   &:hover {
      background-color: ${color.gray[50]};
      text-decoration: none;

      ${Icon} {
         opacity: 1;
         color: ${color.brand[400]};

         :last-child {
            :hover {
               color: ${color.brand[700]};
            }
         }
      }
   }
`;

const LegacyIcon = ({ icon }: { icon: string }) => {
   const resolved = resolveFaLegacyIcon(icon);
   return resolved ? (
      <FaIcon name={resolved.name} style={resolved.style} className="size-[1em]" />
   ) : null;
};

type MetaDataType = {
   date: string;
   url: string;
   icon: string;
   message: string;
};

const NotificationCard = ({
   metadata,
   markRead,
}: {
   metadata: MetaDataType;
   markRead: () => void;
}) => {
   const date = new Date(metadata.date);

   const [relativeDate, setRelativeDate] = useState(getRelativeDate(date));

   useEffect(() => {
      function updateRelativeDate() {
         setRelativeDate(getRelativeDate(date));
      }

      const timerID = setInterval(() => updateRelativeDate(), MINUTE);
      return function cleanup() {
         clearInterval(timerID);
      };
   }, []);

   const onClickGenerator = useCallback(
      (redirect: any) => (e: any) => {
         markRead();
         // A notification whose target document is gone carries no url.
         if (redirect && metadata.url) {
            window.open(metadata.url, '_blank');
         }
         e.stopPropagation();
      },
      [markRead, metadata.url]
   );

   const onKeyDownGenerator = useCallback(
      (redirect: any) => (e: any) => {
         if (e.key === 'Enter') {
            onClickGenerator(redirect)(e);
         }
      },
      [onClickGenerator]
   );

   return (
      <li>
         <NotificationLink onClick={onClickGenerator(true)} onKeyDown={onKeyDownGenerator(true)}>
            <Icon>
               <LegacyIcon icon={metadata.icon} />
            </Icon>
            <NotificationInfo tabIndex={0}>
               <div>{_js(metadata.message)}</div>
               <time title={metadata.date} dateTime={date.toISOString()}>
                  {relativeDate}
               </time>
            </NotificationInfo>
            <Icon
               tabIndex={0}
               onClick={onClickGenerator(false)}
               onKeyDown={onKeyDownGenerator(false)}
            >
               <LegacyIcon icon="times" />
            </Icon>
         </NotificationLink>
      </li>
   );
};

export default NotificationCard;
